import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FavoritesContext from './FavoritesContext';

const STORAGE_KEY = 'favorites';
const FAVORITE_ID_PATTERN = /^(home|design|product):/;

const readFavorites = () => {
  if (typeof window === 'undefined') return [];
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(value)
      ? [...new Set(value.filter((id) => typeof id === 'string' && FAVORITE_ID_PATTERN.test(id)))]
      : [];
  } catch {
    return [];
  }
};

const writeFavorites = (favorites) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch {
    // Keep the current session usable when browser storage is unavailable.
  }
};

export default function FavoritesProvider({ children }) {
  const [favorites, setFavorites] = useState(readFavorites);
  const favoritesRef = useRef(favorites);

  useEffect(() => {
    const syncFavorites = (event) => {
      if (event.key && event.key !== STORAGE_KEY) return;
      const updated = readFavorites();
      favoritesRef.current = updated;
      setFavorites(updated);
    };
    window.addEventListener('storage', syncFavorites);
    return () => window.removeEventListener('storage', syncFavorites);
  }, []);

  const toggleFavorite = useCallback((id) => {
    if (typeof id !== 'string' || !FAVORITE_ID_PATTERN.test(id)) return;
    const current = favoritesRef.current;
    const updated = current.includes(id)
      ? current.filter((favoriteId) => favoriteId !== id)
      : [...current, id];
    favoritesRef.current = updated;
    setFavorites(updated);
    writeFavorites(updated);
  }, []);

  const isFavorite = useCallback((id) => favorites.includes(id), [favorites]);
  const value = useMemo(() => ({ favorites, isFavorite, toggleFavorite }), [favorites, isFavorite, toggleFavorite]);

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}
